import { getFieldName, evaluateScratchInput, evaluateBlockExpression } from "./astEvaluator.js";

/**
 * Core Unified Recursive Scratch Transpiler
 * Transpiles ANY Scratch block across all categories to Python, JS, Arduino C++, or Standard C++.
 */
export const transpileBlock = (startKey, blocks, ctx) => {
  let currKey = startKey;
  
  while (currKey && blocks[currKey]) {
    const b = blocks[currKey];
    const opcode = b.opcode;
    const targetLang = ctx.targetLang;
    const varName = ctx.varName;
    const indent = () => "    ".repeat(ctx.indentLevel || 0);
    
    // Evaluate input helper mapped to targetLang
    const evalInput = (inputName, defaultVal = "0") => {
      return evaluateScratchInput(b, blocks, inputName, targetLang, varName) || defaultVal;
    };

    switch (opcode) {
      // -----------------------------------------------------
      // 1. Events (Yellow)
      // -----------------------------------------------------
      case "event_whenflagclicked": {
        ctx.usedMethods.add("event_flag");
        ctx.eventCounters = ctx.eventCounters || {};
        ctx.eventCounters.flag = (ctx.eventCounters.flag || 0) + 1;
        const count = ctx.eventCounters.flag;
        const funcName = count === 1 ? "when_start" : `when_start_${count}`;
        const jsFuncName = count === 1 ? "whenStart" : `whenStart${count}`;

        if (targetLang === "python") {
          ctx.lines.push(`${indent()}def ${funcName}():`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}async function ${jsFuncName}() {`);
        } else if (targetLang === "arduino") {
          ctx.lines.push(`${indent()}void setup() {`);
          ctx.lines.push(`${indent()}    Serial.begin(9600);`);
        } else if (targetLang === "cpp") {
          ctx.lines.push(`${indent()}int main() {`);
        }
        ctx.activeFunction = {
          opcode,
          funcName: targetLang === "javascript" ? jsFuncName : funcName,
          linesCountAtStart: ctx.lines.length,
          modifiedVars: new Set(),
        };
        ctx.indentLevel += 1;
        break;
      }

      case "event_whenkeypressed": {
        ctx.usedMethods.add("event_key");
        const rawKey = getFieldName(b, "KEY_OPTION") || "space";
        const cleanKey = rawKey.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
        ctx.eventCounters = ctx.eventCounters || {};
        const keyCounterId = `key_${cleanKey}`;
        ctx.eventCounters[keyCounterId] = (ctx.eventCounters[keyCounterId] || 0) + 1;
        const count = ctx.eventCounters[keyCounterId];
        const funcName = count === 1 ? `on_key_${cleanKey}` : `on_key_${cleanKey}_${count}`;
        const jsFuncName = count === 1 ? `onKey${cleanKey.charAt(0).toUpperCase() + cleanKey.slice(1)}` : `onKey${cleanKey.charAt(0).toUpperCase() + cleanKey.slice(1)}${count}`;
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}def ${funcName}():`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}async function ${jsFuncName}() {`);
        }
        ctx.activeFunction = {
          opcode,
          rawKey,
          funcName: targetLang === "javascript" ? jsFuncName : funcName,
          linesCountAtStart: ctx.lines.length,
          modifiedVars: new Set(),
        };
        ctx.indentLevel += 1;
        break;
      }

      case "event_whenthisspriteclicked": {
        ctx.usedMethods.add("event_click");
        ctx.eventCounters = ctx.eventCounters || {};
        const clickCounterId = `click_${varName}`;
        ctx.eventCounters[clickCounterId] = (ctx.eventCounters[clickCounterId] || 0) + 1;
        const count = ctx.eventCounters[clickCounterId];
        const funcName = count === 1 ? `on_${varName}_click` : `on_${varName}_click_${count}`;
        const jsFuncName = count === 1 ? `on${varName.charAt(0).toUpperCase() + varName.slice(1)}Click` : `on${varName.charAt(0).toUpperCase() + varName.slice(1)}Click${count}`;
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}def ${funcName}():`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}async function ${jsFuncName}() {`);
        }
        ctx.activeFunction = {
          opcode,
          funcName: targetLang === "javascript" ? jsFuncName : funcName,
          linesCountAtStart: ctx.lines.length,
          modifiedVars: new Set(),
        };
        ctx.indentLevel += 1;
        break;
      }

      case "event_whenbroadcastreceived": {
        ctx.usedMethods.add("broadcast_received");
        const rawMsg = getFieldName(b, "BROADCAST_OPTION") || "message1";
        const cleanMsg = rawMsg.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
        ctx.eventCounters = ctx.eventCounters || {};
        const bcastCounterId = `bcast_${cleanMsg}`;
        ctx.eventCounters[bcastCounterId] = (ctx.eventCounters[bcastCounterId] || 0) + 1;
        const count = ctx.eventCounters[bcastCounterId];
        const funcName = count === 1 ? `on_receive_${cleanMsg}` : `on_receive_${cleanMsg}_${count}`;
        const jsFuncName = count === 1 ? `onReceive${cleanMsg.charAt(0).toUpperCase() + cleanMsg.slice(1)}` : `onReceive${cleanMsg.charAt(0).toUpperCase() + cleanMsg.slice(1)}${count}`;
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}def ${funcName}():`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}async function ${jsFuncName}() {`);
        }
        ctx.activeFunction = {
          opcode,
          rawMsg,
          funcName: targetLang === "javascript" ? jsFuncName : funcName,
          linesCountAtStart: ctx.lines.length,
          modifiedVars: new Set(),
        };
        ctx.indentLevel += 1;
        break;
      }

      case "event_whenbackdropswitchesto":
      case "event_whenbackdropswitchestocostume": {
        ctx.usedMethods.add("backdrop_switch_event");
        const rawBd = getFieldName(b, "BACKDROP") || "backdrop1";
        const cleanBd = rawBd.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
        ctx.eventCounters = ctx.eventCounters || {};
        const bdCounterId = `bd_${cleanBd}`;
        ctx.eventCounters[bdCounterId] = (ctx.eventCounters[bdCounterId] || 0) + 1;
        const count = ctx.eventCounters[bdCounterId];
        const funcName = count === 1 ? `on_backdrop_${cleanBd}` : `on_backdrop_${cleanBd}_${count}`;
        const jsFuncName = count === 1 ? `onBackdrop${cleanBd.charAt(0).toUpperCase() + cleanBd.slice(1)}` : `onBackdrop${cleanBd.charAt(0).toUpperCase() + cleanBd.slice(1)}${count}`;
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}def ${funcName}():`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}async function ${jsFuncName}() {`);
        }
        ctx.activeFunction = {
          opcode,
          rawBd,
          funcName: targetLang === "javascript" ? jsFuncName : funcName,
          linesCountAtStart: ctx.lines.length,
          modifiedVars: new Set(),
        };
        ctx.indentLevel += 1;
        break;
      }

      case "event_whengreaterthan": {
        ctx.usedMethods.add("greater_than_event");
        const elem = getFieldName(b, "WHENELEMENTMENU") || "loudness";
        const val = evalInput("VALUE", "10");
        const cleanElem = elem.toLowerCase();
        const funcName = `when_${cleanElem}_greater_than_${val}`;
        const jsFuncName = `when${cleanElem.charAt(0).toUpperCase() + cleanElem.slice(1)}GreaterThan${val}`;
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}def ${funcName}():`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}async function ${jsFuncName}() {`);
        }
        ctx.activeFunction = {
          opcode,
          elem,
          val,
          funcName: targetLang === "javascript" ? jsFuncName : funcName,
          linesCountAtStart: ctx.lines.length,
          modifiedVars: new Set(),
        };
        ctx.indentLevel += 1;
        break;
      }
        
      case "event_broadcast": {
        ctx.usedMethods.add("broadcast");
        const msg = evalInput("BROADCAST_INPUT", '"message1"');
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}broadcast(${msg})`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}broadcast(${msg});`);
        } else {
          ctx.lines.push(`${indent()}// broadcast(${msg})`);
        }
        break;
      }

      case "event_broadcastandwait": {
        ctx.usedMethods.add("broadcast");
        const msgWait = evalInput("BROADCAST_INPUT", '"message1"');
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}broadcast_and_wait(${msgWait})`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}await broadcastAndWait(${msgWait});`);
        } else {
          ctx.lines.push(`${indent()}// broadcast_and_wait(${msgWait})`);
        }
        break;
      }

      // -----------------------------------------------------
      // 2. Motion (Blue)
      // -----------------------------------------------------
      case "motion_movesteps": {
        ctx.usedMethods.add("move");
        const steps = evalInput("STEPS", "10");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.move(${steps})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.move(${steps});`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}Serial.println("Moved " + String(${steps}) + " steps");`);
        else if (targetLang === "cpp") ctx.lines.push(`${indent()}std::cout << "${varName} moved " << ${steps} << " steps" << std::endl;`);
        break;
      }

      case "motion_turnright": {
        ctx.usedMethods.add("turn_right");
        const degR = evalInput("DEGREES", "15");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.turn_right(${degR})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.turnRight(${degR});`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}Serial.println("Turned right " + String(${degR}) + " degrees");`);
        else if (targetLang === "cpp") ctx.lines.push(`${indent()}std::cout << "${varName} turned right " << ${degR} << " degrees" << std::endl;`);
        break;
      }

      case "motion_turnleft": {
        ctx.usedMethods.add("turn_left");
        const degL = evalInput("DEGREES", "15");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.turn_left(${degL})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.turnLeft(${degL});`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}Serial.println("Turned left " + String(${degL}) + " degrees");`);
        else if (targetLang === "cpp") ctx.lines.push(`${indent()}std::cout << "${varName} turned left " << ${degL} << " degrees" << std::endl;`);
        break;
      }

      case "motion_goto": {
        ctx.usedMethods.add("go_to");
        const toTarget = evalInput("TO", null);
        if (toTarget && toTarget !== "0") {
          if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.go_to(${toTarget})`);
          else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.goTo(${toTarget});`);
          else ctx.lines.push(`${indent()}// ${varName}.go_to(${toTarget})`);
        } else {
          const x = evalInput("X", "0");
          const y = evalInput("Y", "0");
          if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.go_to(${x}, ${y})`);
          else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.goTo(${x}, ${y});`);
          else if (targetLang === "arduino") ctx.lines.push(`${indent()}Serial.println("Went to x:" + String(${x}) + " y:" + String(${y}));`);
          else if (targetLang === "cpp") ctx.lines.push(`${indent()}std::cout << "${varName} went to x:" << ${x} << " y:" << ${y} << std::endl;`);
        }
        break;
      }

      case "motion_gotoxy": {
        ctx.usedMethods.add("go_to");
        const x = evalInput("X", "0");
        const y = evalInput("Y", "0");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.go_to(${x}, ${y})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.goTo(${x}, ${y});`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}Serial.println("Went to x:" + String(${x}) + " y:" + String(${y}));`);
        else if (targetLang === "cpp") ctx.lines.push(`${indent()}std::cout << "${varName} went to x:" << ${x} << " y:" << ${y} << std::endl;`);
        break;
      }

      case "motion_glideto": {
        ctx.usedMethods.add("glide");
        const gSecsTo = evalInput("SECS", "1");
        const gTarget = evalInput("TO", '"random position"');
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.glide_to(${gSecsTo}, ${gTarget})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}await ${varName}.glideTo(${gSecsTo}, ${gTarget});`);
        else ctx.lines.push(`${indent()}// ${varName}.glide_to(${gSecsTo}, ${gTarget})`);
        break;
      }

      case "motion_glidesecstoxy": {
        ctx.usedMethods.add("glide");
        const gSecs = evalInput("SECS", "1");
        const gX = evalInput("X", "0");
        const gY = evalInput("Y", "0");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.glide(${gSecs}, ${gX}, ${gY})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}await ${varName}.glide(${gSecs}, ${gX}, ${gY});`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}Serial.println("Glided to x:" + String(${gX}) + " y:" + String(${gY}));\n${indent()}delay(${gSecs} * 1000);`);
        else if (targetLang === "cpp") ctx.lines.push(`${indent()}std::cout << "${varName} glided to x:" << ${gX} << " y:" << ${gY} << std::endl;`);
        break;
      }
        
      case "motion_pointindirection": {
        ctx.usedMethods.add("point_in_direction");
        const dir = evalInput("DIRECTION", "90");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.point_in_direction(${dir})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.pointInDirection(${dir});`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}Serial.println("Pointed in direction " + String(${dir}));`);
        else if (targetLang === "cpp") ctx.lines.push(`${indent()}std::cout << "${varName} pointed in direction " << ${dir} << std::endl;`);
        break;
      }

      case "motion_pointtowards": {
        ctx.usedMethods.add("point_towards");
        const towards = evalInput("TOWARDS", '"mouse-pointer"');
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.point_towards(${towards})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.pointTowards(${towards});`);
        else ctx.lines.push(`${indent()}// ${varName}.point_towards(${towards})`);
        break;
      }

      case "motion_changexby": {
        ctx.usedMethods.add("change_x");
        const dx = evalInput("DX", "10");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.change_x(${dx})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.changeX(${dx});`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}Serial.println("Changed X by " + String(${dx}));`);
        else if (targetLang === "cpp") ctx.lines.push(`${indent()}std::cout << "${varName} changed X by " << ${dx} << std::endl;`);
        break;
      }

      case "motion_changeyby": {
        ctx.usedMethods.add("change_y");
        const dy = evalInput("DY", "10");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.change_y(${dy})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.changeY(${dy});`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}Serial.println("Changed Y by " + String(${dy}));`);
        else if (targetLang === "cpp") ctx.lines.push(`${indent()}std::cout << "${varName} changed Y by " << ${dy} << std::endl;`);
        break;
      }
        
      case "motion_setx": {
        ctx.usedMethods.add("set_x");
        const setx = evalInput("X", "0");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.set_x(${setx})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.setX(${setx});`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}// ${varName} set X to ${setx}`);
        else if (targetLang === "cpp") ctx.lines.push(`${indent()}// ${varName} set X to ${setx}`);
        break;
      }
        
      case "motion_sety": {
        ctx.usedMethods.add("set_y");
        const sety = evalInput("Y", "0");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.set_y(${sety})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.setY(${sety});`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}// ${varName} set Y to ${sety}`);
        else if (targetLang === "cpp") ctx.lines.push(`${indent()}// ${varName} set Y to ${sety}`);
        break;
      }
        
      case "motion_ifonedgebounce": {
        ctx.usedMethods.add("bounce");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.bounce_if_on_edge()`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.bounceIfOnEdge();`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}// ${varName} bounce if on edge`);
        else if (targetLang === "cpp") ctx.lines.push(`${indent()}// ${varName} bounce if on edge`);
        break;
      }

      case "motion_setrotationstyle": {
        ctx.usedMethods.add("rotation_style");
        const style = getFieldName(b, "STYLE") || "all around";
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.set_rotation_style("${style}")`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.setRotationStyle("${style}");`);
        else ctx.lines.push(`${indent()}// set rotation style to ${style}`);
        break;
      }

      // -----------------------------------------------------
      // 3. Looks (Purple)
      // -----------------------------------------------------
      case "looks_say":
      case "looks_sayforsecs": {
        ctx.usedMethods.add("say");
        const sayMsg = evalInput("MESSAGE", '"Hello!"');
        if (opcode === "looks_sayforsecs") {
          const sSecs = evalInput("SECS", "2");
          if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.say(${sayMsg}, ${sSecs})`);
          else if (targetLang === "javascript") ctx.lines.push(`${indent()}await ${varName}.say(${sayMsg}, ${sSecs});`);
          else if (targetLang === "arduino") {
            ctx.lines.push(`${indent()}Serial.println(String("[${varName}] Says: ") + String(${sayMsg}));`);
            ctx.lines.push(`${indent()}delay(${sSecs} * 1000);`);
          } else if (targetLang === "cpp") {
            ctx.lines.push(`${indent()}std::cout << "[${varName}] Says: " << ${sayMsg} << std::endl;`);
          }
        } else {
          if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.say(${sayMsg})`);
          else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.say(${sayMsg});`);
          else if (targetLang === "arduino") ctx.lines.push(`${indent()}Serial.println(String("[${varName}] Says: ") + String(${sayMsg}));`);
          else if (targetLang === "cpp") ctx.lines.push(`${indent()}std::cout << "[${varName}] Says: " << ${sayMsg} << std::endl;`);
        }
        break;
      }

      case "looks_think":
      case "looks_thinkforsecs": {
        ctx.usedMethods.add("think");
        const thkMsg = evalInput("MESSAGE", '"Hmm..."');
        if (opcode === "looks_thinkforsecs") {
          const tSecs = evalInput("SECS", "2");
          if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.think(${thkMsg}, ${tSecs})`);
          else if (targetLang === "javascript") ctx.lines.push(`${indent()}await ${varName}.think(${thkMsg}, ${tSecs});`);
          else if (targetLang === "arduino") {
            ctx.lines.push(`${indent()}Serial.println(String("[${varName}] Thinks: ") + String(${thkMsg}));`);
            ctx.lines.push(`${indent()}delay(${tSecs} * 1000);`);
          } else if (targetLang === "cpp") {
            ctx.lines.push(`${indent()}std::cout << "[${varName}] Thinks: " << ${thkMsg} << std::endl;`);
          }
        } else {
          if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.think(${thkMsg})`);
          else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.think(${thkMsg});`);
          else if (targetLang === "arduino") ctx.lines.push(`${indent()}Serial.println(String("[${varName}] Thinks: ") + String(${thkMsg}));`);
          else if (targetLang === "cpp") ctx.lines.push(`${indent()}std::cout << "[${varName}] Thinks: " << ${thkMsg} << std::endl;`);
        }
        break;
      }

      case "looks_show": {
        ctx.usedMethods.add("show");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.show()`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.show();`);
        else ctx.lines.push(`${indent()}// ${varName}.show()`);
        break;
      }

      case "looks_hide": {
        ctx.usedMethods.add("hide");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.hide()`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.hide();`);
        else ctx.lines.push(`${indent()}// ${varName}.hide()`);
        break;
      }
        
      case "looks_switchcostumeto": {
        ctx.usedMethods.add("switch_costume");
        const costume = evalInput("COSTUME", '"costume1"');
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.switch_costume(${costume})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.switchCostume(${costume});`);
        else ctx.lines.push(`${indent()}// switch costume to ${costume}`);
        break;
      }
        
      case "looks_nextcostume": {
        ctx.usedMethods.add("next_costume");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.next_costume()`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.nextCostume();`);
        else ctx.lines.push(`${indent()}// next costume`);
        break;
      }

      case "looks_switchbackdropto":
      case "looks_switchbackdroptoandwait": {
        ctx.usedMethods.add("switch_backdrop");
        const bd = evalInput("BACKDROP", '"backdrop1"');
        if (targetLang === "python") ctx.lines.push(`${indent()}stage.switch_backdrop(${bd})`);
        else if (targetLang === "javascript") {
          if (opcode === "looks_switchbackdroptoandwait") {
            ctx.lines.push(`${indent()}await stage.switchBackdropAndWait(${bd});`);
          } else {
            ctx.lines.push(`${indent()}stage.switchBackdrop(${bd});`);
          }
        } else ctx.lines.push(`${indent()}// switch backdrop to ${bd}`);
        break;
      }

      case "looks_nextbackdrop": {
        ctx.usedMethods.add("next_backdrop");
        if (targetLang === "python") ctx.lines.push(`${indent()}stage.next_backdrop()`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}stage.nextBackdrop();`);
        else ctx.lines.push(`${indent()}// next backdrop`);
        break;
      }
        
      case "looks_changesizeby": {
        ctx.usedMethods.add("change_size");
        const changeSize = evalInput("CHANGE", "10");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.change_size(${changeSize})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.changeSize(${changeSize});`);
        else ctx.lines.push(`${indent()}// change size by ${changeSize}`);
        break;
      }

      case "looks_setsizeto": {
        ctx.usedMethods.add("set_size");
        const setSize = evalInput("SIZE", "100");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.set_size(${setSize})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.setSize(${setSize});`);
        else ctx.lines.push(`${indent()}// set size to ${setSize}%`);
        break;
      }

      case "looks_changeeffectby": {
        ctx.usedMethods.add("change_effect");
        const effect = (getFieldName(b, "EFFECT") || "color").toLowerCase();
        const change = evalInput("CHANGE", "25");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.change_effect("${effect}", ${change})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.changeEffect("${effect}", ${change});`);
        else ctx.lines.push(`${indent()}// change effect ${effect} by ${change}`);
        break;
      }

      case "looks_seteffectto": {
        ctx.usedMethods.add("set_effect");
        const effectSet = (getFieldName(b, "EFFECT") || "color").toLowerCase();
        const valSet = evalInput("VALUE", "0");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.set_effect("${effectSet}", ${valSet})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.setEffect("${effectSet}", ${valSet});`);
        else ctx.lines.push(`${indent()}// set effect ${effectSet} to ${valSet}`);
        break;
      }

      case "looks_cleargraphiceffects":
      case "looks_cleargrapheffects": {
        ctx.usedMethods.add("clear_effects");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.clear_graphic_effects()`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.clearGraphicEffects();`);
        else ctx.lines.push(`${indent()}// clear graphic effects`);
        break;
      }

      case "looks_gotofrontback": {
        const fb = getFieldName(b, "FRONT_BACK") || "front";
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.go_to_layer("${fb}")`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.goToLayer("${fb}");`);
        else ctx.lines.push(`${indent()}// go to ${fb} layer`);
        break;
      }

      case "looks_goforwardbackwardlayers": {
        const dir = getFieldName(b, "FORWARD_BACKWARD") || "forward";
        const layers = evalInput("NUM", "1");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.change_layer("${dir}", ${layers})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.changeLayer("${dir}", ${layers});`);
        else ctx.lines.push(`${indent()}// go ${dir} ${layers} layers`);
        break;
      }

      // -----------------------------------------------------
      // 4. Sound (Pink)
      // -----------------------------------------------------
      case "sound_play":
      case "sound_playuntildone": {
        ctx.usedMethods.add("play_sound");
        const sound = evalInput("SOUND_MENU", '"Meow"');
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.play_sound(${sound})`);
        else if (targetLang === "javascript") {
          if (opcode === "sound_playuntildone") {
            ctx.lines.push(`${indent()}await ${varName}.playSoundUntilDone(${sound});`);
          } else {
            ctx.lines.push(`${indent()}${varName}.playSound(${sound});`);
          }
        } else if (targetLang === "arduino") {
          ctx.lines.push(`${indent()}// Play sound: ${sound}`);
          ctx.lines.push(`${indent()}tone(8, 440, 500); // 440Hz for 500ms`);
        } else if (targetLang === "cpp") {
          ctx.lines.push(`${indent()}std::cout << "[${varName}] Playing sound: " << ${sound} << std::endl;`);
        }
        break;
      }
        
      case "sound_stopallsounds": {
        ctx.usedMethods.add("stop_sounds");
        if (targetLang === "python") ctx.lines.push(`${indent()}stop_all_sounds()`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}stopAllSounds();`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}noTone(8);`);
        else ctx.lines.push(`${indent()}// stop all sounds`);
        break;
      }

      case "sound_changeeffectby": {
        const sndEff = (getFieldName(b, "EFFECT") || "pitch").toLowerCase();
        const sndVal = evalInput("VALUE", "10");
        if (targetLang === "python") ctx.lines.push(`${indent()}sound.change_effect("${sndEff}", ${sndVal})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}sound.changeEffect("${sndEff}", ${sndVal});`);
        else ctx.lines.push(`${indent()}// sound change effect ${sndEff} by ${sndVal}`);
        break;
      }

      case "sound_seteffectto": {
        const sndEffSet = (getFieldName(b, "EFFECT") || "pitch").toLowerCase();
        const sndValSet = evalInput("VALUE", "100");
        if (targetLang === "python") ctx.lines.push(`${indent()}sound.set_effect("${sndEffSet}", ${sndValSet})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}sound.setEffect("${sndEffSet}", ${sndValSet});`);
        else ctx.lines.push(`${indent()}// sound set effect ${sndEffSet} to ${sndValSet}`);
        break;
      }

      case "sound_cleareffects": {
        if (targetLang === "python") ctx.lines.push(`${indent()}sound.clear_effects()`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}sound.clearEffects();`);
        else ctx.lines.push(`${indent()}// sound clear effects`);
        break;
      }

      case "sound_changevolumeby": {
        const volChange = evalInput("VOLUME", "-10");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.change_volume(${volChange})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.changeVolume(${volChange});`);
        else ctx.lines.push(`${indent()}// change volume by ${volChange}`);
        break;
      }

      case "sound_setvolumeto": {
        const volSet = evalInput("VOLUME", "100");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.volume = ${volSet}`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.volume = ${volSet};`);
        else ctx.lines.push(`${indent()}// set volume to ${volSet}%`);
        break;
      }

      // -----------------------------------------------------
      // 5. Control (Orange)
      // -----------------------------------------------------
      case "control_if": {
        const condIf = evalInput("CONDITION", "true");
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}if ${condIf}:`);
          if (b.inputs && b.inputs.SUBSTACK && b.inputs.SUBSTACK[1]) {
            transpileBlock(b.inputs.SUBSTACK[1], blocks, { ...ctx, indentLevel: ctx.indentLevel + 1, isSubstack: true });
          } else {
            ctx.lines.push(`${indent()}    pass`);
          }
        } else {
          ctx.lines.push(`${indent()}if (${condIf}) {`);
          if (b.inputs && b.inputs.SUBSTACK && b.inputs.SUBSTACK[1]) {
            transpileBlock(b.inputs.SUBSTACK[1], blocks, { ...ctx, indentLevel: ctx.indentLevel + 1, isSubstack: true });
          }
          ctx.lines.push(`${indent()}}`);
        }
        break;
      }

      case "control_if_else": {
        const condIfElse = evalInput("CONDITION", "true");
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}if ${condIfElse}:`);
          if (b.inputs && b.inputs.SUBSTACK && b.inputs.SUBSTACK[1]) {
            transpileBlock(b.inputs.SUBSTACK[1], blocks, { ...ctx, indentLevel: ctx.indentLevel + 1, isSubstack: true });
          } else {
            ctx.lines.push(`${indent()}    pass`);
          }
          ctx.lines.push(`${indent()}else:`);
          if (b.inputs && b.inputs.SUBSTACK2 && b.inputs.SUBSTACK2[1]) {
            transpileBlock(b.inputs.SUBSTACK2[1], blocks, { ...ctx, indentLevel: ctx.indentLevel + 1, isSubstack: true });
          } else {
            ctx.lines.push(`${indent()}    pass`);
          }
        } else {
          ctx.lines.push(`${indent()}if (${condIfElse}) {`);
          if (b.inputs && b.inputs.SUBSTACK && b.inputs.SUBSTACK[1]) {
            transpileBlock(b.inputs.SUBSTACK[1], blocks, { ...ctx, indentLevel: ctx.indentLevel + 1, isSubstack: true });
          }
          ctx.lines.push(`${indent()}} else {`);
          if (b.inputs && b.inputs.SUBSTACK2 && b.inputs.SUBSTACK2[1]) {
            transpileBlock(b.inputs.SUBSTACK2[1], blocks, { ...ctx, indentLevel: ctx.indentLevel + 1, isSubstack: true });
          }
          ctx.lines.push(`${indent()}}`);
        }
        break;
      }

      case "control_repeat": {
        const times = evalInput("TIMES", "10");
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}for _ in range(int(${times})):`);
          if (b.inputs && b.inputs.SUBSTACK && b.inputs.SUBSTACK[1]) {
            transpileBlock(b.inputs.SUBSTACK[1], blocks, { ...ctx, indentLevel: ctx.indentLevel + 1, isSubstack: true });
          } else {
            ctx.lines.push(`${indent()}    pass`);
          }
        } else if (targetLang === "javascript") {
          const loopVar = `i_${Math.random().toString(36).substr(2, 5)}`;
          ctx.lines.push(`${indent()}for (let ${loopVar} = 0; ${loopVar} < ${times}; ${loopVar}++) {`);
          if (b.inputs && b.inputs.SUBSTACK && b.inputs.SUBSTACK[1]) {
            transpileBlock(b.inputs.SUBSTACK[1], blocks, { ...ctx, indentLevel: ctx.indentLevel + 1, isSubstack: true });
          }
          ctx.lines.push(`${indent()}}`);
        } else { // Arduino / C++
          const loopVar = `i_${Math.random().toString(36).substr(2, 5)}`;
          ctx.lines.push(`${indent()}for (int ${loopVar} = 0; ${loopVar} < ${times}; ${loopVar}++) {`);
          if (b.inputs && b.inputs.SUBSTACK && b.inputs.SUBSTACK[1]) {
            transpileBlock(b.inputs.SUBSTACK[1], blocks, { ...ctx, indentLevel: ctx.indentLevel + 1, isSubstack: true });
          }
          ctx.lines.push(`${indent()}}`);
        }
        break;
      }

      case "control_forever": {
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}while True:`);
          if (b.inputs && b.inputs.SUBSTACK && b.inputs.SUBSTACK[1]) {
            transpileBlock(b.inputs.SUBSTACK[1], blocks, { ...ctx, indentLevel: ctx.indentLevel + 1, isSubstack: true });
          } else {
            ctx.lines.push(`${indent()}    time.sleep(0.1)`);
          }
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}while (true) {`);
          if (b.inputs && b.inputs.SUBSTACK && b.inputs.SUBSTACK[1]) {
            transpileBlock(b.inputs.SUBSTACK[1], blocks, { ...ctx, indentLevel: ctx.indentLevel + 1, isSubstack: true });
          }
          ctx.lines.push(`${indent()}    await delay(100); // Prevent infinite loop lock`);
          ctx.lines.push(`${indent()}}`);
        } else if (targetLang === "arduino") {
          ctx.lines.push(`${indent()}// In Arduino, void loop() already loops forever.`);
          ctx.lines.push(`${indent()}while (true) {`);
          if (b.inputs && b.inputs.SUBSTACK && b.inputs.SUBSTACK[1]) {
            transpileBlock(b.inputs.SUBSTACK[1], blocks, { ...ctx, indentLevel: ctx.indentLevel + 1, isSubstack: true });
          }
          ctx.lines.push(`${indent()}}`);
        } else {
          ctx.lines.push(`${indent()}while (true) {`);
          if (b.inputs && b.inputs.SUBSTACK && b.inputs.SUBSTACK[1]) {
            transpileBlock(b.inputs.SUBSTACK[1], blocks, { ...ctx, indentLevel: ctx.indentLevel + 1, isSubstack: true });
          }
          ctx.lines.push(`${indent()}}`);
        }
        break;
      }
        
      case "control_repeat_until": {
        const condUntil = evalInput("CONDITION", "false");
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}while not ${condUntil}:`);
          if (b.inputs && b.inputs.SUBSTACK && b.inputs.SUBSTACK[1]) {
            transpileBlock(b.inputs.SUBSTACK[1], blocks, { ...ctx, indentLevel: ctx.indentLevel + 1, isSubstack: true });
          } else {
            ctx.lines.push(`${indent()}    time.sleep(0.1)`);
          }
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}while (!(${condUntil})) {`);
          if (b.inputs && b.inputs.SUBSTACK && b.inputs.SUBSTACK[1]) {
            transpileBlock(b.inputs.SUBSTACK[1], blocks, { ...ctx, indentLevel: ctx.indentLevel + 1, isSubstack: true });
          }
          ctx.lines.push(`${indent()}    await delay(100);`);
          ctx.lines.push(`${indent()}}`);
        } else { // Arduino / C++
          ctx.lines.push(`${indent()}while (!(${condUntil})) {`);
          if (b.inputs && b.inputs.SUBSTACK && b.inputs.SUBSTACK[1]) {
            transpileBlock(b.inputs.SUBSTACK[1], blocks, { ...ctx, indentLevel: ctx.indentLevel + 1, isSubstack: true });
          }
          ctx.lines.push(`${indent()}}`);
        }
        break;
      }

      case "control_wait": {
        const waitSecs = evalInput("DURATION", "1");
        if (targetLang === "python") ctx.lines.push(`${indent()}time.sleep(${waitSecs})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}await delay(${waitSecs} * 1000);`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}delay(${waitSecs} * 1000);`);
        else if (targetLang === "cpp") ctx.lines.push(`${indent()}sleep(${waitSecs});`);
        break;
      }
        
      case "control_wait_until": {
        const condWaitUntil = evalInput("CONDITION", "false");
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}while not ${condWaitUntil}: # wait_until`);
          ctx.lines.push(`${indent()}    time.sleep(0.1)`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}while (!(${condWaitUntil})) { // wait_until`);
          ctx.lines.push(`${indent()}    await delay(100);`);
          ctx.lines.push(`${indent()}}`);
        } else {
          ctx.lines.push(`${indent()}while (!(${condWaitUntil})) { delay(100); }`);
        }
        break;
      }
        
      case "control_stop": {
        const stopOpt = evalInput("STOP_OPTION", '"all"');
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}exit(0) # Stop ${stopOpt}`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}return; // Stop ${stopOpt}`);
        } else if (targetLang === "arduino") {
          ctx.lines.push(`${indent()}while(1); // Stop ${stopOpt}`);
        } else {
          ctx.lines.push(`${indent()}return 0; // Stop ${stopOpt}`);
        }
        break;
      }

      case "control_start_as_clone": {
        ctx.usedMethods.add("clone_start");
        ctx.eventCounters = ctx.eventCounters || {};
        const cloneCounterId = `clone_${varName}`;
        ctx.eventCounters[cloneCounterId] = (ctx.eventCounters[cloneCounterId] || 0) + 1;
        const count = ctx.eventCounters[cloneCounterId];
        const funcName = count === 1 ? "on_start_as_clone" : `on_start_as_clone_${count}`;
        const jsFuncName = count === 1 ? "onStartAsClone" : `onStartAsClone${count}`;
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}def ${funcName}():`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}async function ${jsFuncName}() {`);
        } else {
          ctx.lines.push(`${indent()}void ${funcName}() {`);
        }
        ctx.activeFunction = {
          opcode,
          funcName: targetLang === "javascript" ? jsFuncName : funcName,
          linesCountAtStart: ctx.lines.length,
          modifiedVars: new Set(),
        };
        ctx.indentLevel += 1;
        break;
      }
        
      case "control_create_clone_of": {
        ctx.usedMethods.add("clone");
        const cloneTarget = evalInput("CLONE_OPTION", '"myself"');
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.create_clone(${cloneTarget})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.createClone(${cloneTarget});`);
        else ctx.lines.push(`${indent()}// clone ${cloneTarget}`);
        break;
      }

      case "control_delete_this_clone": {
        ctx.usedMethods.add("delete_clone");
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.delete_this_clone()`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.deleteThisClone();`);
        else ctx.lines.push(`${indent()}// delete this clone`);
        break;
      }

      // -----------------------------------------------------
      // 6. Sensing (Light Blue)
      // -----------------------------------------------------
      case "sensing_askandwait": {
        const askMsg = evalInput("QUESTION", '"What\'s your name?"');
        if (targetLang === "python") ctx.lines.push(`${indent()}user_answer = input(${askMsg})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}user_answer = await prompt(${askMsg});`);
        else if (targetLang === "arduino") {
          ctx.lines.push(`${indent()}Serial.println(${askMsg});`);
          ctx.lines.push(`${indent()}while(Serial.available() == 0) { }`);
          ctx.lines.push(`${indent()}String user_answer = Serial.readString();`);
        } else if (targetLang === "cpp") {
          ctx.lines.push(`${indent()}std::cout << ${askMsg} << std::endl;`);
          ctx.lines.push(`${indent()}std::string user_answer;`);
          ctx.lines.push(`${indent()}std::cin >> user_answer;`);
        }
        break;
      }
        
      case "sensing_resettimer": {
        ctx.usedMethods.add("reset_timer");
        if (targetLang === "python") ctx.lines.push(`${indent()}timer_start = time.time()`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}timerStart = Date.now();`);
        else ctx.lines.push(`${indent()}// reset timer`);
        break;
      }

      case "sensing_setdragmode": {
        const dragMode = getFieldName(b, "DRAG_MODE") || "draggable";
        if (targetLang === "python") ctx.lines.push(`${indent()}${varName}.set_drag_mode("${dragMode}")`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${varName}.setDragMode("${dragMode}");`);
        else ctx.lines.push(`${indent()}// set drag mode ${dragMode}`);
        break;
      }

      // -----------------------------------------------------
      // 7. Variables & Lists (Orange)
      // -----------------------------------------------------
      case "data_setvariableto": {
        const rawVarName = getFieldName(b, "VARIABLE");
        const cleanVar = rawVarName.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
        const val = evalInput("VALUE", "0");
        if (targetLang === "python") {
          if (ctx.activeFunction && ctx.activeFunction.modifiedVars) {
            if (!ctx.activeFunction.argNames || !ctx.activeFunction.argNames.includes(cleanVar)) {
              ctx.activeFunction.modifiedVars.add(cleanVar);
            }
          }
          ctx.lines.push(`${indent()}${cleanVar} = ${val}`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}${cleanVar} = ${val};`);
        } else if (targetLang === "arduino") {
          ctx.lines.push(`${indent()}${cleanVar} = ${val};`);
        } else if (targetLang === "cpp") {
          ctx.lines.push(`${indent()}${cleanVar} = ${val};`);
        }
        break;
      }

      case "data_changevariableby": {
        const rawVarName = getFieldName(b, "VARIABLE");
        const cleanVar = rawVarName.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
        const val = evalInput("VALUE", "1");
        if (targetLang === "python") {
          if (ctx.activeFunction && ctx.activeFunction.modifiedVars) {
            if (!ctx.activeFunction.argNames || !ctx.activeFunction.argNames.includes(cleanVar)) {
              ctx.activeFunction.modifiedVars.add(cleanVar);
            }
          }
          ctx.lines.push(`${indent()}${cleanVar} += ${val}`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}${cleanVar} += ${val};`);
        } else if (targetLang === "arduino") {
          ctx.lines.push(`${indent()}${cleanVar} += ${val};`);
        } else if (targetLang === "cpp") {
          ctx.lines.push(`${indent()}${cleanVar} += ${val};`);
        }
        break;
      }

      case "data_showvariable": {
        const rawVarName = getFieldName(b, "VARIABLE");
        const cleanVar = rawVarName.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
        if (targetLang === "python") ctx.lines.push(`${indent()}print(f"${rawVarName}: {${cleanVar}}")`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}console.log("${rawVarName}:", ${cleanVar});`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}Serial.println(String("${rawVarName}: ") + String(${cleanVar}));`);
        else if (targetLang === "cpp") ctx.lines.push(`${indent()}std::cout << "${rawVarName}: " << ${cleanVar} << std::endl;`);
        break;
      }
      
      case "data_hidevariable": {
        const rawVarNameHide = getFieldName(b, "VARIABLE");
        ctx.lines.push(`${indent()}// hide variable ${rawVarNameHide}`);
        break;
      }

      case "data_addtolist": {
        const item = evalInput("ITEM", '"item"');
        const listName = getFieldName(b, "LIST").replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
        if (targetLang === "python") ctx.lines.push(`${indent()}${listName}.append(${item})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${listName}.push(${item});`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}// ${listName}.add(${item});`);
        else if (targetLang === "cpp") ctx.lines.push(`${indent()}${listName}.push_back(${item});`);
        break;
      }

      case "data_deleteoflist": {
        const idxDel = evalInput("INDEX", "1");
        const listNameDel = getFieldName(b, "LIST").replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
        if (targetLang === "python") ctx.lines.push(`${indent()}del ${listNameDel}[int(${idxDel}) - 1]`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${listNameDel}.splice(${idxDel} - 1, 1);`);
        else ctx.lines.push(`${indent()}// delete ${idxDel} of ${listNameDel}`);
        break;
      }

      case "data_deletealloflist": {
        const listNameClr = getFieldName(b, "LIST").replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
        if (targetLang === "python") ctx.lines.push(`${indent()}${listNameClr}.clear()`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${listNameClr}.length = 0;`);
        else ctx.lines.push(`${indent()}// delete all of ${listNameClr}`);
        break;
      }

      case "data_insertatlist": {
        const itemIns = evalInput("ITEM", '"thing"');
        const idxIns = evalInput("INDEX", "1");
        const listNameIns = getFieldName(b, "LIST").replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
        if (targetLang === "python") ctx.lines.push(`${indent()}${listNameIns}.insert(int(${idxIns}) - 1, ${itemIns})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${listNameIns}.splice(${idxIns} - 1, 0, ${itemIns});`);
        else ctx.lines.push(`${indent()}// insert ${itemIns} at ${idxIns} of ${listNameIns}`);
        break;
      }

      case "data_replaceitemoflist": {
        const idxRep = evalInput("INDEX", "1");
        const itemRep = evalInput("ITEM", '"thing"');
        const listNameRep = getFieldName(b, "LIST").replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
        if (targetLang === "python") ctx.lines.push(`${indent()}${listNameRep}[int(${idxRep}) - 1] = ${itemRep}`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}${listNameRep}[${idxRep} - 1] = ${itemRep};`);
        else ctx.lines.push(`${indent()}// replace item ${idxRep} of ${listNameRep} with ${itemRep}`);
        break;
      }

      case "data_showlist": {
        const listName = getFieldName(b, "LIST").replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
        if (targetLang === "python") ctx.lines.push(`${indent()}print(f"${listName}: {${listName}}")`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}console.log("${listName}:", ${listName});`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}// print ${listName}`);
        else if (targetLang === "cpp") ctx.lines.push(`${indent()}// print ${listName}`);
        break;
      }

      case "data_hidelist": {
        const listNameHide = getFieldName(b, "LIST");
        ctx.lines.push(`${indent()}// hide list ${listNameHide}`);
        break;
      }
      
      // -----------------------------------------------------
      // 8. Custom Blocks (Procedures)
      // -----------------------------------------------------
      case "procedures_definition": {
        ctx.usedMethods.add("procedure_def");
        const protoId = b.inputs && b.inputs.custom_block && b.inputs.custom_block[1];
        const protoBlock = protoId && blocks[protoId];
        let funcName = "custom_block";
        let argNames = [];
        if (protoBlock && protoBlock.mutation) {
          const proccode = protoBlock.mutation.proccode || "";
          const cleanName = proccode.replace(/%[snb]/g, "").trim().replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
          if (cleanName) funcName = cleanName;
          try {
            argNames = JSON.parse(protoBlock.mutation.argumentnames || "[]").map(a => a.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase());
          } catch(e) {}
        } else {
          funcName = `custom_block_${b.id.substring(0,5).replace(/[^a-zA-Z0-9]/g, "")}`;
        }
        const argsStr = argNames.join(", ");
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}def ${funcName}(${argsStr}):`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}async function ${funcName}(${argsStr}) {`);
        } else {
          ctx.lines.push(`${indent()}void ${funcName}(${argsStr}) {`);
        }
        ctx.activeFunction = {
          opcode,
          funcName,
          argNames,
          linesCountAtStart: ctx.lines.length,
          modifiedVars: new Set(),
        };
        ctx.indentLevel += 1;
        break;
      }
        
      case "procedures_call": {
        ctx.usedMethods.add("procedure_call");
        let callName = "custom_block";
        let callArgs = [];
        if (b.mutation) {
          const proccode = b.mutation.proccode || "";
          const cleanName = proccode.replace(/%[snb]/g, "").trim().replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
          if (cleanName) callName = cleanName;
          try {
            const argIds = JSON.parse(b.mutation.argumentids || "[]");
            callArgs = argIds.map(argId => {
              return evaluateScratchInput(b, blocks, argId, targetLang, varName) || "0";
            });
          } catch(e) {}
        }
        const argsStr = callArgs.join(", ");
        if (targetLang === "python") ctx.lines.push(`${indent()}${callName}(${argsStr})`);
        else if (targetLang === "javascript") ctx.lines.push(`${indent()}await ${callName}(${argsStr});`);
        else if (targetLang === "arduino") ctx.lines.push(`${indent()}${callName}(${argsStr});`);
        else if (targetLang === "cpp") ctx.lines.push(`${indent()}${callName}(${argsStr});`);
        break;
      }

      // -----------------------------------------------------
      // 8b. Google Teachable Machine AI Extension Blocks
      // -----------------------------------------------------
      case "teachableMachine_whenClassDetected": {
        ctx.usedMethods.add("tm_when_class");
        let rawClass = evalInput("CLASS", '"Thumbs Up"');
        rawClass = String(rawClass).replace(/^["']|["']$/g, "") || getFieldName(b, "CLASS") || "Thumbs Up";
        const cleanCls = rawClass.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase() || "class_1";
        ctx.eventCounters = ctx.eventCounters || {};
        const tmCounterId = `tm_${cleanCls}`;
        ctx.eventCounters[tmCounterId] = (ctx.eventCounters[tmCounterId] || 0) + 1;
        const count = ctx.eventCounters[tmCounterId];
        const funcName = count === 1 ? `on_class_${cleanCls}` : `on_class_${cleanCls}_${count}`;
        const pascalCls = cleanCls
          .split("_")
          .filter(Boolean)
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join("");
        const jsFuncName = count === 1 ? `onClass${pascalCls}` : `onClass${pascalCls}${count}`;

        if (targetLang === "python") {
          ctx.lines.push(`${indent()}def ${funcName}():`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}async function ${jsFuncName}() {`);
        }
        ctx.activeFunction = {
          opcode,
          rawClass,
          funcName: targetLang === "javascript" ? jsFuncName : funcName,
          linesCountAtStart: ctx.lines.length,
          modifiedVars: new Set(),
        };
        ctx.indentLevel += 1;
        break;
      }

      case "teachableMachine_loadModelUrl": {
        const modelUrl = evalInput("URL", '"https://teachablemachine.withgoogle.com/models/.../"');
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}tm.load_model(${modelUrl})`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}tm.loadModel(${modelUrl});`);
        }
        break;
      }

      case "teachableMachine_setVideoState": {
        let vState = evalInput("VIDEO_STATE", '"on"');
        if (!vState || vState === "0") vState = `"${getFieldName(b, "VIDEO_STATE") || "on"}"`;
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}tm.set_video(${vState})`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}tm.setVideo(${vState});`);
        }
        break;
      }

      case "teachableMachine_setVideoTransparency": {
        const transp = evalInput("TRANSPARENCY", "25");
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}tm.set_video_transparency(${transp})`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}tm.setVideoTransparency(${transp});`);
        }
        break;
      }

      case "teachableMachine_setConfidenceThreshold": {
        const thresh = evalInput("THRESHOLD", "75");
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}tm.set_confidence_threshold(${thresh})`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}tm.setConfidenceThreshold(${thresh});`);
        }
        break;
      }

      // -----------------------------------------------------
      // 9. Fallback & Universal Standalone Block Handler
      // -----------------------------------------------------
      default: {
        // If a student places a Reporter or Boolean block loose on the canvas:
        const evaluatedExpr = evaluateBlockExpression(b, blocks, targetLang, varName);
        if (evaluatedExpr && evaluatedExpr !== "0" && evaluatedExpr !== '""') {
          const readableLabel = opcode.replace(/^[^_]+_/, "").replace(/_/g, " ");
          if (targetLang === "python") {
            ctx.lines.push(`${indent()}# Standalone Block Value:`);
            ctx.lines.push(`${indent()}print("${readableLabel}:", ${evaluatedExpr})`);
          } else if (targetLang === "javascript") {
            ctx.lines.push(`${indent()}// Standalone Block Value:`);
            ctx.lines.push(`${indent()}console.log("${readableLabel}:", ${evaluatedExpr});`);
          } else {
            ctx.lines.push(`${indent()}std::cout << "${readableLabel}: " << ${evaluatedExpr} << std::endl;`);
          }
        } else {
          const cleanOpcode = opcode.replace(/_/g, ".");
          if (targetLang === "python") ctx.lines.push(`${indent()}# Block: ${cleanOpcode}`);
          else ctx.lines.push(`${indent()}// Block: ${cleanOpcode}`);
        }
        break;
      }
    }

    // Move to next block in the stack
    currKey = b.next;
    
    // Close functions for Events/Procedures if we reached the end of the top-level stack
    if (!currKey && ctx.activeFunction && !ctx.isSubstack) {
      const af = ctx.activeFunction;
      // If function body is completely empty (no child blocks), add 'pass' in Python
      if (ctx.lines.length === af.linesCountAtStart) {
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}pass`);
        }
      } else if (targetLang === "python" && af.modifiedVars && af.modifiedVars.size > 0) {
        const indentStr = "    ".repeat(ctx.indentLevel);
        ctx.lines.splice(af.linesCountAtStart, 0, `${indentStr}global ${Array.from(af.modifiedVars).join(", ")}`);
      }

      if (targetLang === "javascript" || targetLang === "arduino" || targetLang === "cpp") {
        ctx.indentLevel = Math.max(0, ctx.indentLevel - 1);
        ctx.lines.push(`${indent()}}`);
      } else {
        ctx.indentLevel = Math.max(0, ctx.indentLevel - 1);
      }

      if (
        af.opcode.startsWith("event_") ||
        af.opcode === "control_start_as_clone" ||
        af.opcode === "teachableMachine_whenClassDetected"
      ) {
        ctx.lines.push("");
      }

      // Emit event callback registration below function definition
      if (af.opcode === "event_whenflagclicked") {
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}${varName}.when_start(${af.funcName})`);
          ctx.lines.push(`${indent()}${af.funcName}()`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}${varName}.whenStart(${af.funcName});`);
          ctx.lines.push(`${indent()}${af.funcName}();`);
        }
      } else if (af.opcode === "event_whenkeypressed") {
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}${varName}.when_key_pressed("${af.rawKey}", ${af.funcName})`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}${varName}.whenKeyPressed("${af.rawKey}", ${af.funcName});`);
        }
      } else if (af.opcode === "event_whenthisspriteclicked") {
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}${varName}.when_clicked(${af.funcName})`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}${varName}.whenClicked(${af.funcName});`);
        }
      } else if (af.opcode === "event_whenbroadcastreceived") {
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}${varName}.when_broadcast_received("${af.rawMsg}", ${af.funcName})`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}${varName}.whenBroadcastReceived("${af.rawMsg}", ${af.funcName});`);
        }
      } else if (af.opcode === "event_whenbackdropswitchesto" || af.opcode === "event_whenbackdropswitchestocostume") {
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}stage.when_backdrop_switch("${af.rawBd}", ${af.funcName})`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}stage.whenBackdropSwitch("${af.rawBd}", ${af.funcName});`);
        }
      } else if (af.opcode === "event_whengreaterthan") {
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}${varName}.when_greater_than("${af.elem}", ${af.val}, ${af.funcName})`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}${varName}.whenGreaterThan("${af.elem}", ${af.val}, ${af.funcName});`);
        }
      } else if (af.opcode === "control_start_as_clone") {
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}${varName}.when_start_as_clone(${af.funcName})`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}${varName}.whenStartAsClone(${af.funcName});`);
        }
      } else if (af.opcode === "teachableMachine_whenClassDetected") {
        if (targetLang === "python") {
          ctx.lines.push(`${indent()}tm.when_class_detected("${af.rawClass}", ${af.funcName})`);
        } else if (targetLang === "javascript") {
          ctx.lines.push(`${indent()}tm.whenClassDetected("${af.rawClass}", ${af.funcName});`);
        }
      }

      ctx.activeFunction = null;
    }
  }
};
